(() => {
  'use strict';

  if (window.__ziqingSupportController) {
    window.__ziqingSupportController.init(document);
    return;
  }

  const instances = new WeakMap();
  const families = new Set(['all', 'blood', 'oxygen', 'nebulizer', 'prenatal', 'hearing', 'accessories']);
  const within = (scope, selector) => [
    ...(scope.matches?.(selector) ? [scope] : []),
    ...scope.querySelectorAll(selector),
  ];
  // Model punctuation must not make WH-02, WH 02 and WH02 different searches.
  const normalize = (value) => String(value || '').normalize('NFKC').toLowerCase().replace(/(\d)\s*×\s*(?=aaa?\b)/g, '$1').replace(/[\s\-\u2010-\u2015]+/g, '');
  function modelShaped(value) {
    if (!/^[a-z0-9\s\-\u2010-\u2015]+$/i.test(value) || !/^[a-z]{1,10}\d[a-z0-9]{0,15}$/.test(normalize(value))) return false;
    const parts = value.trim().split(/[\s\-\u2010-\u2015]+/);
    const firstDigit = parts.findIndex((part) => /\d/.test(part));
    return parts.slice(firstDigit + 1).every((part) => /^(?:\d+[a-z]{0,2}|[a-z]{1,2})$/i.test(part));
  }
  function tokensFor(query) {
    const value = String(query || '').normalize('NFKC').replace(/(\d)\s*×\s*(?=aaa?\b)/gi, '$1').trim();
    const edition = value.match(/^(.*?)\s*(?:\(\s*(LCD|LED)\s*\)|\s+(LCD|LED))$/i);
    if (edition && modelShaped(edition[1].trim())) return [normalize(edition[1]), normalize(edition[2] || edition[3])];
    // A spaced model is one model, not unrelated letter fragments (e.g. the
    // P in ZY P 01 must not match the P in every document's PDF label).
    if (modelShaped(value)) return [normalize(value)];
    return value.split(/\s+/).map(normalize).filter(Boolean);
  }
  const modelKey = (value) => /^[a-z]{1,10}\d[a-z0-9]{0,15}$/.test(value);
  const connectiveWords = new Set(['a', 'an', 'and', 'for', 'of', 'the']);
  const ordinaryPrefixes = new Set(['manual', 'manuals', 'user', 'guide', 'guides', 'instructions', 'english', 'pdf', 'model', 'models', 'find', 'show', 'help', 'lcd', 'led', 'type', 'usb', ...connectiveWords]);
  const queryText = (value) => String(value || '').normalize('NFKC').replace(/[\u2010-\u2015]/g, '-').replace(/(\d)\s*(?:×\s*)?(?=aaa?\b)/gi, '$1');
  const modelEntries = (value) => String(value || '').split(/[|/]/).map((entry) => entry.trim().replace(/\s*\((?:LCD|LED)\)\s*$/i, '')).filter((entry) => modelKey(normalize(entry)));
  const modelKeys = (entries) => {
    const keys = new Set();
    entries.forEach((entry) => {
      // Only a real separator in an explicitly listed model permits a parent.
      // TX-05-01 permits TX-05, never the unrelated TX-01 or a bare TX0 prefix.
      const parts = entry.split(/[-\u2010-\u2015]/).map((part) => part.trim()).filter(Boolean);
      for (let size = 1; size <= parts.length; size += 1) {
        const key = normalize(parts.slice(0, size).join('-'));
        if (modelKey(key)) keys.add(key);
      }
    });
    return keys;
  };
  function parseQuery(query, letterPrefixes) {
    const value = queryText(query);
    const words = [...value.matchAll(/[a-z0-9]+(?:-[a-z0-9]+)*/gi)].map((match) => ({ text: match[0], start: match.index, end: match.index + match[0].length }));
    const models = [];
    // Keep punctuation within a model span intact until it is normalized.
    // Otherwise TX/01 becomes unrelated tx + 01 terms and can match TX-05-01.
    // The set mirrors punctuation removed from ordinary terms below; letters
    // are never separators, so complete parallel models remain separate.
    const adjacent = (left, right) => /^[\s(),;:/&+\-]*$/.test(value.slice(left.end, right.start));
    const fragmentKey = (text) => normalize(text.replace(/[(),;:/&+]+/g, ''));
    const batteryToken = (word) => /^\d+aaa?$/i.test(word);
    for (let index = 0; index < words.length; index += 1) {
      const first = words[index];
      let end = index;
      let key = normalize(first.text);
      if (!modelKey(key)) {
        if (!/^[a-z]{1,10}$/i.test(first.text) || ordinaryPrefixes.has(key)) continue;
        let found = false;
        for (let next = index + 1; next < Math.min(words.length, index + 3); next += 1) {
          if (!adjacent(words[next - 1], words[next]) || batteryToken(words[next].text)) break;
          const candidate = fragmentKey(value.slice(first.start, words[next].end));
          const prefix = candidate.match(/^([a-z]+)\d/i)?.[1];
          if (prefix) {
            // A second alphabetic segment must come from the actual registry
            // (SJ OX1C / ZY P 01), not from ordinary words such as "for TX 05".
            if (modelKey(candidate) && (prefix === normalize(first.text) || letterPrefixes.has(prefix))) {
              key = candidate;
              end = next;
              found = true;
            }
            break;
          }
          if (!/^[a-z]+$/i.test(candidate) || ![...letterPrefixes].some((prefixValue) => prefixValue.startsWith(candidate))) break;
        }
        if (!found) continue;
      }
      while (end + 1 < words.length && adjacent(words[end], words[end + 1]) && /^\d+[a-z]{0,2}$/i.test(words[end + 1].text) && !batteryToken(words[end + 1].text)) {
        const extended = fragmentKey(value.slice(first.start, words[end + 1].end));
        if (!modelKey(extended)) break;
        key = extended;
        end += 1;
      }
      models.push({ key, value: value.slice(first.start, words[end].end).trim(), start: first.start, end: words[end].end });
      index = end;
    }
    let cursor = 0;
    const remaining = [];
    models.forEach((model) => { remaining.push(value.slice(cursor, model.start)); cursor = model.end; });
    remaining.push(value.slice(cursor));
    // Model spans have already been extracted. Classifying the remaining
    // ordinary words as another model would fuse "LCD 4 AAA" into lcd4aaa.
    const terms = remaining.join(' ').replace(/[(),;:/&+]+/g, ' ').split(/\s+/).map(normalize).filter(Boolean).filter((term) => !models.length || !connectiveWords.has(term));
    return { models, terms };
  }
  const boundedQuery = (value) => Array.from(String(value || '')).slice(0, 200).join('');
  const focus = (element) => {
    if (element?.isConnected) element.focus({ preventScroll: true });
  };
  const modelQuery = (query, models) => {
    // This exact store reference is consultation context, not a manual alias.
    if (/^640B(?: store listing)?$/i.test(String(query || '').trim())) return '640B store listing';
    // Validate the whole input before extracting a model: do not turn markup,
    // URLs or injected query parameters into a seemingly safe contact request.
    if (!/^[A-Za-z0-9 ()-]+$/.test(queryText(query).trim()) || models.length !== 1) return '';
    // A detached short suffix may still belong to the device model (for
    // example SJ OX 1 C). Leave the generic enquiry intact, never drop it.
    const detachedSuffix = queryText(query).slice(models[0].end).match(/^[\s-]+([A-Za-z]{1,2})(?=$|[\s()-])/);
    if (detachedSuffix && !connectiveWords.has(detachedSuffix[1].toLowerCase())) return '';
    // The display edition is part of a useful manual request. Never reduce
    // an unmatched XB05 LED request to the ambiguous base model XB05.
    const editions = [...new Set([...queryText(query).matchAll(/\b(LCD|LED)\b/gi)].map((match) => match[1].toUpperCase()))];
    if (editions.length > 1) return '';
    const edition = editions[0];
    const value = edition && !new RegExp(`\\b${edition}\\b`, 'i').test(models[0].value) ? `${models[0].value} ${edition}` : models[0].value;
    return value.length <= 31 && /^[A-Za-z0-9 -]+$/.test(value) ? value : '';
  };

  const initRoot = (root) => {
    if (instances.has(root)) return;
    const controller = new AbortController();
    const listen = (element, type, handler) => element?.addEventListener(type, handler, { signal: controller.signal });
    const own = (selector) => [...root.querySelectorAll(selector)].filter((element) => element.closest('[data-zs-root]') === root);
    // Each section owns its already-translated complete sentences. Replace only
    // known numeric slots as plain text; never parse translations as model data.
    const message = (name, values) => String(root.dataset[name] || '').replace(/__([A-Z_]+)__/g, (token, key) => Object.prototype.hasOwnProperty.call(values, key) ? String(values[key]) : token);
    const input = own('[data-zs-search]')[0];
    const cards = own('[data-zs-card]');
    const faqs = own('[data-zs-faq]');
    const faqGroups = own('[data-zs-faq-group]');
    const familyButtons = own('[data-zs-family]');
    const dialogs = own('[data-zs-dialog]');
    const requestLinks = own('[data-zs-request]').map((link) => ({ link, href: link.getAttribute('href') || '/pages/support-center#contact' }));
    const libraryLinks = own('[data-zs-library-link]').map((link) => ({ link, href: link.getAttribute('href') || '' }));
    const searchIndex = new Map([...cards, ...faqs].map((item) => [item, normalize(item.dataset.search || item.textContent)]));
    const exactModels = new Map(cards.map((card) => [card, modelEntries(card.dataset.models)]));
    const modelIndex = new Map(cards.map((card) => [card, modelKeys(exactModels.get(card))]));
    const letterPrefixes = new Set([...exactModels.values()].flat().map((entry) => normalize(entry).match(/^([a-z]+)\d/)?.[1]).filter(Boolean));
    const returnTargets = new WeakMap();
    const requestedLimit = Number(root.dataset.zsLimit);
    const limit = Number.isInteger(requestedLimit) && requestedLimit > 0 ? requestedLimit : Infinity;
    let expanded = false;
    let activeDialog = null;
    let family = 'all';
    let restoreFrame = null;
    let destroyed = false;
    const entryURL = new URL(window.location.href);
    const samePageURL = () => {
      try {
        const url = new URL(window.location.href);
        return ['http:', 'https:'].includes(url.protocol) && url.origin === entryURL.origin && url.pathname === entryURL.pathname ? url : null;
      } catch (_) { return null; }
    };
    const readURL = () => {
      const url = samePageURL();
      if (!url) return false;
      family = families.has(url.searchParams.get('family')) ? url.searchParams.get('family') : 'all';
      expanded = url.searchParams.get('manuals_expanded') === '1';
      if (input) input.value = boundedQuery(url.searchParams.get('q'));
      return true;
    };
    const persist = () => {
      const url = samePageURL();
      if (!url || destroyed) return;
      const values = { q: input?.value || '', family: family === 'all' ? '' : family, manuals_expanded: expanded ? '1' : '' };
      Object.entries(values).forEach(([key, value]) => { if (value) url.searchParams.set(key, value); else url.searchParams.delete(key); });
      if (url.href === window.location.href) return;
      try { window.history.replaceState(window.history.state, '', url.href); } catch (_) { /* Filtering remains available in a restricted preview. */ }
    };
    const localLink = (href, values) => {
      try {
        const url = new URL(href, window.location.href);
        if (!href || !['http:', 'https:'].includes(url.protocol) || url.origin !== entryURL.origin || url.username || url.password) return href;
        Object.entries(values).forEach(([key, value]) => { if (value) url.searchParams.set(key, value); else url.searchParams.delete(key); });
        return url.href;
      } catch (_) { return href; }
    };
    readURL();

    const render = () => {
      if (input) input.value = boundedQuery(input.value);
      const query = input?.value.trim() || '';
      const parsed = parseQuery(query, letterPrefixes);
      const matchesText = (item, terms) => terms.every((term) => searchIndex.get(item).includes(term));
      const matchedCards = cards.filter((card) => (family === 'all' || card.dataset.family === family) && parsed.models.every((model) => modelIndex.get(card).has(model.key)) && matchesText(card, parsed.terms));
      const showAll = expanded || !!query || family !== 'all';
      const visibleCards = new Set(showAll ? matchedCards : matchedCards.slice(0, limit));
      cards.forEach((card) => { card.hidden = !visibleCards.has(card); });
      faqs.forEach((faq) => { faq.hidden = !matchesText(faq, [...parsed.models.map((model) => model.key), ...parsed.terms]); });
      const faqCount = faqs.filter((faq) => !faq.hidden).length;
      faqGroups.forEach((group) => {
        const children = faqs.filter((faq) => group.contains(faq));
        group.hidden = children.length > 0 && children.every((faq) => faq.hidden);
      });
      familyButtons.forEach((button) => {
        const selected = button.dataset.zsFamily === family;
        button.setAttribute('aria-pressed', String(selected));
        button.classList.toggle('is-active', selected);
      });
      own('[data-zs-count]').forEach((status) => { status.textContent = message('zsCountTemplate', { COUNT: matchedCards.length }); });
      own('[data-zs-empty]').forEach((empty) => { empty.hidden = matchedCards.length !== 0; });
      own('[data-zs-faq-empty]').forEach((empty) => { empty.hidden = faqCount !== 0; });
      own('[data-zs-more]').forEach((button) => { button.hidden = showAll || matchedCards.length <= limit; });
      own('[data-zs-clear]').forEach((button) => { button.hidden = !input?.value; });
      own('[data-zs-query-status]').forEach((status) => {
        status.textContent = faqs.length
          ? message('zsResultsFaqTemplate', { COUNT: matchedCards.length, FAQ_COUNT: faqCount })
          : message('zsResultsTemplate', { COUNT: matchedCards.length });
      });
      const model = matchedCards.length === 0 ? modelQuery(query, parsed.models) : '';
      requestLinks.forEach(({ link, href }) => {
        link.setAttribute('href', model ? localLink(href, { model }) : href);
      });
      libraryLinks.forEach(({ link, href }) => {
        // Carry only this library's chosen query/category; never copy the
        // current page's unrelated parameters into a different destination.
        link.setAttribute('href', input?.value || family !== 'all' ? localLink(href, { q: input?.value || '', family: family === 'all' ? '' : family }) : href);
      });
    };
    const afterNativeRestore = () => {
      if (restoreFrame !== null) window.cancelAnimationFrame(restoreFrame);
      restoreFrame = window.requestAnimationFrame(() => {
        restoreFrame = null;
        if (!destroyed && root.isConnected && readURL()) render();
      });
    };
    listen(window, 'pageshow', afterNativeRestore);
    listen(window, 'popstate', afterNativeRestore);

    const stopVideo = (dialog) => {
      dialog.querySelectorAll('video[data-zs-local-video]').forEach((video) => { video.pause(); video.currentTime = 0; });
      dialog.querySelectorAll('iframe[data-zs-video-src]').forEach((frame) => frame.removeAttribute('src'));
    };
    const loadVideo = (dialog) => {
      dialog.querySelectorAll('video[data-zs-local-video]').forEach((video) => { video.preload = 'metadata'; video.load(); });
      dialog.querySelectorAll('iframe[data-zs-video-src]').forEach((frame) => {
        const source = frame.dataset.zsVideoSrc || '';
        if (/^https:\/\/www\.youtube-nocookie\.com\/embed\/[A-Za-z0-9_-]{11}$/.test(source)) frame.setAttribute('src', source);
        else frame.removeAttribute('src');
      });
    };
    const restoreFocus = (dialog) => {
      stopVideo(dialog);
      if (activeDialog === dialog) activeDialog = null;
      const opener = returnTargets.get(dialog);
      returnTargets.delete(dialog);
      if (!root.isConnected) return;
      focus(opener?.isConnected && !opener.closest('[hidden]') ? opener : input);
    };
    const closeDialog = (dialog) => {
      if (!dialog) return;
      stopVideo(dialog);
      if (dialog.open) dialog.close();
    };
    dialogs.forEach((dialog) => {
      dialog.querySelectorAll('video[data-zs-local-video]').forEach((video) => {
        const error = dialog.querySelector('[data-zs-video-error]');
        listen(video, 'error', () => { if (error) error.hidden = false; });
        listen(video, 'loadedmetadata', () => { if (error) error.hidden = true; });
        listen(dialog.querySelector('[data-zs-video-retry]'), 'click', () => { if (error) error.hidden = true; video.load(); });
      });
      listen(dialog, 'cancel', (event) => {
        event.preventDefault();
        closeDialog(dialog);
      });
      listen(dialog, 'close', () => restoreFocus(dialog));
      listen(dialog, 'click', (event) => {
        if (event.target !== dialog) return;
        const rect = dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) closeDialog(dialog);
      });
    });

    listen(root, 'input', (event) => {
      if (event.target === input) { render(); persist(); }
    });
    listen(root, 'submit', (event) => {
      if (!event.target.matches('[data-zs-search-form]')) return;
      event.preventDefault();
      render();
      persist();
      const manuals = root.querySelector('#manuals');
      if (!manuals) return;
      if (!manuals.hasAttribute('tabindex')) manuals.setAttribute('tabindex', '-1');
      focus(manuals);
      const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
      manuals.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'start' });
    });
    listen(root, 'click', (event) => {
      const control = event.target.closest?.('[data-zs-family], [data-zs-clear], [data-zs-more], [data-zs-open], [data-zs-close]');
      if (!control || control.closest('[data-zs-root]') !== root) return;
      if (control.hasAttribute('data-zs-family')) {
        if (!families.has(control.dataset.zsFamily)) return;
        event.preventDefault();
        family = control.dataset.zsFamily;
        render();
        persist();
      } else if (control.hasAttribute('data-zs-clear')) {
        event.preventDefault();
        if (input) input.value = '';
        family = 'all';
        expanded = false;
        render();
        persist();
        focus(input);
      } else if (control.hasAttribute('data-zs-more')) {
        event.preventDefault();
        const firstNewCard = cards.find((card) => card.hidden);
        expanded = true;
        render();
        persist();
        if (firstNewCard && !firstNewCard.hidden) {
          const target = firstNewCard.querySelector('[data-zs-open], a[href], button') || firstNewCard;
          if (target === firstNewCard && !target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
          focus(target);
        }
      } else if (control.hasAttribute('data-zs-open')) {
        const dialog = dialogs.find((item) => item.id === control.dataset.zsOpen);
        if (!dialog || dialog.open) return;
        event.preventDefault();
        if (typeof dialog.showModal !== 'function') {
          dialog.querySelector('a[href]')?.click();
          return;
        }
        if (activeDialog?.open) closeDialog(activeDialog);
        returnTargets.set(dialog, control);
        activeDialog = dialog;
        try {
          dialog.showModal();
          if (dialog.open) loadVideo(dialog);
        } catch (_) {
          returnTargets.delete(dialog);
          activeDialog = null;
          dialog.querySelector('a[href]')?.click();
        }
      } else {
        event.preventDefault();
        const dialog = control.closest('[data-zs-dialog]');
        if (dialogs.includes(dialog)) closeDialog(dialog);
      }
    });

    instances.set(root, () => {
      destroyed = true;
      controller.abort();
      if (restoreFrame !== null) window.cancelAnimationFrame(restoreFrame);
      restoreFrame = null;
      dialogs.forEach(closeDialog);
      activeDialog = null;
      // A retained section may be initialized again; never capture our derived
      // model/query URLs as its authored fallback on the next initialization.
      [...requestLinks, ...libraryLinks].forEach(({ link, href }) => link.setAttribute('href', href));
      instances.delete(root);
    });
    render();
  };

  const init = (scope = document) => within(scope, '[data-zs-root]').forEach(initRoot);
  window.__ziqingSupportController = { init };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  else init();
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    within(event.target, '[data-zs-root]').forEach((root) => instances.get(root)?.());
  });
})();
